import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { GlobalHotKeys } from 'react-hotkeys';

import styled from 'styled-components';
import { Box, Grid, Button } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';

import CallIcon from '@material-ui/icons/Call';
import SmsIcon from '@material-ui/icons/Sms';
import EmailIcon from '@material-ui/icons/Email';
import LoopIcon from '@material-ui/icons/Loop';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import ArchiveIcon from '@material-ui/icons/Archive';

import TextWithLabel from 'components/shared/TextWithLabel';
import IconBorder from 'components/shared/IconBorder';

import { RootState } from 'state/store';
import { apiMarkLeadAsDone } from 'state/lead.state';
import { formatAddress, formatDate, formatPhoneNumber, formatTimezone } from 'utils/functions';
import { Lead, ObjectLiteral, Colors, LeadTag, LeadType } from 'typings';

dayjs.extend(relativeTime);

interface LeadProps {
  lead: Lead;
  isCallCenterRoutingEnabled: boolean;
}

interface LeadTypeIndicatorProps {
  color: string;
}

const Wrapper = styled('div')`
  && {
    > * + * {
      border-top: 1px solid ${(props) => props.theme.palette.grey['200']};
    }

    > .MuiGrid-container {
      > .MuiGrid-grid-sm-6 + .MuiGrid-grid-sm-6 {
        border-left: 1px solid ${(props) => props.theme.palette.grey['200']};
      }
    }
  }
`;

const LeadTypeIndicator = styled('span').attrs<LeadTypeIndicatorProps>({})`
  display: inline-block;
  height: ${(props) => props.theme.spacing(1.5)};
  width: ${(props) => props.theme.spacing(1.5)};
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

const useStyles = makeStyles((theme: Theme) => ({
  btnWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: theme.spacing(-1.5),
    marginBottom: theme.spacing(-2),
  },
  btnDone: {
    backgroundColor: theme.palette.background.green,
    color: Colors.WHITE,
    borderTopLeftRadius: '15px',
    padding: theme.spacing(0.5, 3),
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

const DisplayLeadDetails = ({ lead, isCallCenterRoutingEnabled }: LeadProps) => {
  const dispatch = useDispatch();
  const isDoneActionEligible = useSelector((state: RootState) => state.lead.isDoneActionEligible);
  const isAssignedLead = useSelector(
    (state: RootState) => state.concierge.assignedLeadId === lead.id
  );
  const isLeadSummaryStatusChanged = useSelector(
    (state: RootState) => state.lead.isLeadStatusChanged
  );
  const canMarkDone = isDoneActionEligible && isAssignedLead && !isLeadSummaryStatusChanged;

  const classes = useStyles();
  const leadIcon = lead?.icon || 'unknown';

  const handleDoneButtonClick = () => {
    dispatch(apiMarkLeadAsDone());
  };

  const hotKeyHandlers = {
    DONE: handleDoneButtonClick,
  };

  // @ts-ignore
  const Icon: OverridableComponent<SvgIconTypeMap> = {
    sms: SmsIcon,
    call: CallIcon,
    email: EmailIcon,
    auto: LoopIcon,
    general: ErrorIcon,
    unknown: HourglassEmptyIcon,
    archive: ArchiveIcon,
  }[leadIcon];

  const getLeadTagTooltip = (leadIcon: string, leadTag: LeadTag | string): string => {
    const tooltip: { [key: string]: ObjectLiteral } = {
      sms: {
        [LeadTag.URGENT]: 'Red - Reply by SMS',
        [LeadTag.AWAITING_RESPONSE]: 'Orange - Reply by SMS',
        [LeadTag.ACTION_REQUIRED]: 'Blue - Unresponsive, followup with Lead or Continue Auto',
        [LeadTag.ONGOING_CHAT]: 'Grey - Ongoing Chat',
      },
      call: {
        [LeadTag.URGENT]: 'Red - Double Dial Lead',
        [LeadTag.AWAITING_RESPONSE]: 'Orange - Double Dial Lead',
        [LeadTag.ACTION_REQUIRED]: 'Blue - Single dial the lead',
        [LeadTag.INBOUND_CALL]: 'Purple - Accept call; if missed, call the lead back',
        [LeadTag.ONGOING_CHAT]: 'Grey - Ongoing Chat',
      },
      email: {
        [LeadTag.AWAITING_RESPONSE]: 'Orange - Reply by Email',
        [LeadTag.ACTION_REQUIRED]:
          'Blue - Unresponsive, followup with Lead by Email or Continue Automation',
      },
      auto: {
        [LeadTag.AUTOMATION]: 'Continue Automation',
      },
      general: {
        [LeadTag.URGENT]: 'Internal: Follow Red (!) Process',
      },
      archive: {
        [LeadTag.ARCHIVE]: 'Archived - lead has been qualified / unqualified',
        [LeadTag.CONVO_ENDED]:
          'Light Gray - Conversation has ended as part of internal mass end process.',
      },
    };

    return tooltip[leadIcon]?.[leadTag] ?? 'Unknown';
  };

  const phoneLabel = lead?.phoneCarrierType ? `phone (${lead.phoneCarrierType})` : 'phone';

  const getLeadTagColor = (leadTag?: LeadTag | string): string => {
    const tagColors: { [key: string]: string } = {
      [LeadTag.URGENT]: Colors.RED,
      [LeadTag.AWAITING_RESPONSE]: Colors.ORANGE,
      [LeadTag.ACTION_REQUIRED]: Colors.LIGHT_BLUE,
      [LeadTag.INBOUND_CALL]: Colors.PURPLE,
      [LeadTag.ONGOING_CHAT]: Colors.MEDIUM_GRAY,
      [LeadTag.AUTOMATION]: Colors.MEDIUM_GRAY,
      [LeadTag.ARCHIVE]: Colors.MEDIUM_GRAY,
      [LeadTag.CONVO_ENDED]: Colors.LIGHT_GRAY,
    };

    return leadTag ? tagColors[leadTag] : Colors.MEDIUM_GRAY;
  };

  const getLeadTypeColor = (
    isCallCenterRoutingEnabled: boolean,
    leadType?: LeadType | string
  ): string => {
    if (isCallCenterRoutingEnabled) {
      return Colors.PURPLE;
    }

    const leadTypeColors: { [key: string]: string } = {
      [LeadType.MORTGAGE]: Colors.LIGHT_PURPLE,
      [LeadType.REFI]: Colors.LIGHT_PURPLE,
      [LeadType.BUYER]: Colors.LIGHT_GREEN,
      [LeadType.SELLER]: Colors.LIGHT_GREEN,
    };

    return leadType && leadType in leadTypeColors ? leadTypeColors[leadType] : Colors.LIGHT_ORANGE;
  };

  const renderLocalTime = (): string => {
    if (!lead?.timezone) return 'N/A';

    const time = formatDate(Date.now(), 'h:mm A', lead.timezone) || 'N/A';
    let timezone;
    if (lead.timezone === 'America/Phoenix') {
      timezone = 'AZ';
    } else {
      timezone = formatTimezone(lead.timezone, Date.now());
    }

    const timezoneFormatted = timezone ? ` (${timezone})` : '';

    return `${time}${timezoneFormatted}`;
  };

  return (
    <Wrapper data-testid="display-lead-details">
      <div className="horizontal-space">
        <TextWithLabel label="name">
          {lead?.firstName} {lead?.lastName}
        </TextWithLabel>
        <Tooltip title={getLeadTagTooltip(leadIcon, lead?.tag)}>
          <IconBorder backgroundColor={getLeadTagColor(lead?.tag)}>
            <Icon htmlColor={Colors.WHITE} />
          </IconBorder>
        </Tooltip>
      </div>
      <Grid container>
        <Grid item xs={12} sm={6}>
          <TextWithLabel label={`type${isCallCenterRoutingEnabled ? ' - callconnect' : ''}`}>
            <LeadTypeIndicator
              color={getLeadTypeColor(isCallCenterRoutingEnabled, lead?.leadType || undefined)}
            />{' '}
            {lead?.leadType}
          </TextWithLabel>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextWithLabel label="source">{lead?.channelWebsite}</TextWithLabel>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={6}>
          <TextWithLabel label="created">{dayjs().to(dayjs.utc(lead?.createdAt))}</TextWithLabel>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextWithLabel label="local time">{renderLocalTime()}</TextWithLabel>
        </Grid>
      </Grid>
      <div className="horizontal-space">
        <TextWithLabel label={phoneLabel}>
          {formatPhoneNumber(lead?.phone || undefined) || 'N/A'}
        </TextWithLabel>
      </div>
      <div className="horizontal-space">
        <TextWithLabel label="email">{lead?.email}</TextWithLabel>
      </div>
      <Box>
        <TextWithLabel label="address">
          {formatAddress(lead?.street, lead?.city, lead?.state, lead?.postalCode) || 'N/A'}
        </TextWithLabel>
        {canMarkDone && (
          <div className={classes.btnWrapper}>
            <Button className={classes.btnDone} type="button" onClick={handleDoneButtonClick}>
              Done
            </Button>
            <GlobalHotKeys handlers={hotKeyHandlers} />
          </div>
        )}
      </Box>
    </Wrapper>
  );
};

export default DisplayLeadDetails;
